import React, { PureComponent } from "react";
import styled from "styled-components";
import { getRequest, postRequest, patchRequest } from "../../api/ApiService";
import Auth from "../../routes/Auth";

// Instructor-only "Survey Ready to Review" popup. Fires on login when
// at least one finished peer-eval survey (initial or final) exists
// AND insights haven't been generated for it yet.
//
// Click "Generate Insights" runs four server steps back-to-back
// (fires for both initial and final peer-eval surveys):
//   1a) POST /v2/forms/surveys/<survey>/calc-score/
//       (peer-score calculation from the latest feedback)
//   1b) POST /v2/games/usergrade/run/  { game, survey } form-encoded
//       (per-user grade compile — needs 1a's peer scores to be fresh)
//   2)  GET  /v2/insights/survey/<survey>/students/?page=1&page_size=1
//       (short-circuit if insights are already there)
//   3)  POST /v2/insights/generate/<survey>/              (insights job)
//       Then poll the returned status_url until status === 'done'.
//
// A progress bar advances through the four stages so the instructor
// sees continuous motion. Poll cadence + timeouts mirror the existing
// dashboard.js flow (10s / 30s / 60s tiers, 6-minute cap).

const Backdrop = styled.div`
  position: fixed;
  top: 59px;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 27, 59, 0.55);
  z-index: 1500;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const Card = styled.div`
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.25);
  width: 100%;
  max-width: 520px;
  padding: 32px 36px;
  text-align: center;
  font-family: 'Mukta Vaani', sans-serif;
`;

const Title = styled.h2`
  font-weight: 700;
  font-size: 22px;
  line-height: 30px;
  color: rgba(0, 27, 59, 0.94);
  margin: 0 0 12px;
`;

const Description = styled.p`
  font-size: 16px;
  line-height: 24px;
  color: rgba(0, 27, 59, 0.78);
  margin: 0 0 24px;
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
`;

const PrimaryButton = styled.button`
  background: #2DA548;
  border: 1px solid #2DA548;
  border-radius: 4px;
  padding: 10px 28px;
  font-family: 'Mukta Vaani', sans-serif;
  font-weight: 600;
  font-size: 16px;
  line-height: 20px;
  color: #ffffff;
  cursor: pointer;

  &:hover { background: #248339; border-color: #248339; }
  &:disabled { background: #a4d9b1; border-color: #a4d9b1; cursor: default; }
`;

const ProgressWrap = styled.div`
  margin-top: 24px;
  text-align: left;
`;

const ProgressLabel = styled.div`
  font-size: 14px;
  color: rgba(0, 27, 59, 0.7);
  margin-bottom: 8px;
`;

const ProgressTrack = styled.div`
  width: 100%;
  height: 8px;
  background: hsla(213, 100%, 12%, 0.08);
  border-radius: 999px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: #2DA548;
  width: ${p => Math.max(0, Math.min(100, p.pct))}%;
  transition: width 0.4s ease;
`;

const ErrorMsg = styled.div`
  margin-top: 16px;
  font-size: 14px;
  color: #d34c4c;
`;

// Poll tiers — same shape as the roster-upload + dashboard flow.
const POLL_FIRST_DELAY_MS = 10 * 1000;
const POLL_MID_DELAY_MS = 30 * 1000;
const POLL_LATE_DELAY_MS = 60 * 1000;
const POLL_MID_THRESHOLD_MS = 2 * 60 * 1000;
const POLL_LATE_THRESHOLD_MS = 4 * 60 * 1000;
const POLL_MAX_DURATION_MS = 6 * 60 * 1000;

// Progress percentage per stage. Polling grows over time within its
// slice so the bar visibly moves.
const STAGE_PROGRESS = {
  idle: 0,
  'running-score': 10,
  'checking-insights': 20,
  'generating': 30,
  'polling-min': 30,
  'polling-max': 95,
  'done': 100,
};

// Map peer-eval survey stage → course_members field that gates the
// popup and gets PATCHed once the instructor clicks Generate.
const VIEWED_FIELD_FOR_STAGE = {
  initial: 'initial_survey_viewed',
  final: 'final_survey_viewed',
};

class InstructorSurveyReadyNotification extends PureComponent {
  state = {
    checked: false,
    targetSurvey: null,
    gameId: null,
    // Instructor's own course_members row id — needed for the PATCH
    // that marks the viewed flag true once Generate is clicked.
    courseMemberId: null,
    stage: 'idle',
    errorMessage: null,
    pollElapsedMs: 0,
    dismissed: false,
  };

  componentDidMount() {
    this.evaluate();
  }

  componentDidUpdate(prevProps) {
    // Props arrive from Dashboard after its own general-info / survey-
    // data fetches resolve. If evaluate() ran before props landed, it
    // bailed early; re-run once allSurveys becomes available so we
    // don't miss the popup on first mount.
    if (this.state.checked) return;
    const prevReady = Array.isArray(prevProps.allSurveys) && prevProps.allSurveys.length > 0;
    const nextReady = Array.isArray(this.props.allSurveys) && this.props.allSurveys.length > 0;
    if (!prevReady && nextReady) {
      this.evaluate();
    }
  }

  componentWillUnmount() {
    this._stopPolling();
    if (this._pollTicker) clearInterval(this._pollTicker);
  }

  evaluate = async () => {
    const courseId = Auth.getCourseId();
    const gameId = Auth.getGameId();
    const userId = Auth.getUserId();
    if (!courseId || !gameId || !userId) {
      this.setState({ checked: true });
      return;
    }

    // All four decision inputs are available in state the Dashboard
    // already loaded on mount — no dedicated surveys/ or course_members/
    // fetch at mount time:
    //   - props.allSurveys                (from /app/survey-data/)
    //   - props.courseMembers[0]          (from general-info; carries
    //                                      covers_all_sections for the
    //                                      Co-Instructor / IAS branch)
    //   - Auth.getUser().position         (lead vs assistant / co)
    //   - Auth.getUser().sections         (section assignments, if any)
    //   - Auth.getUser().viewed_results   (from general-info data.user)
    const allSurveys = Array.isArray(this.props.allSurveys) ? this.props.allSurveys : [];
    const courseMembers = Array.isArray(this.props.courseMembers) ? this.props.courseMembers : [];
    const authUser = Auth.getUser() || {};
    const viewedResults = (authUser.viewed_results && typeof authUser.viewed_results === 'object')
      ? authUser.viewed_results
      : {};

    // Wait for Dashboard to hydrate before deciding; componentDidUpdate
    // re-runs this when allSurveys arrives.
    if (allSurveys.length === 0) return;

    // Peer evaluation + insight generation is a course-level action.
    // Allowed to see this popup:
    //   1. Lead instructor (position === "Instructor") with no
    //      section assignments on their user record (full course lead).
    //   2. Co-Instructor OR Instructor Assistant whose course_members
    //      row has covers_all_sections === true.
    // Section-scoped assistants / co-instructors stay suppressed.
    const isLeadInstructor = authUser.position === "Instructor";
    const hasSectionAssignments = Array.isArray(authUser.sections)
      && authUser.sections.length > 0;
    const selfSummary = courseMembers[0];
    const coversAll = selfSummary && selfSummary.covers_all_sections === true;
    const isAssistantOrCo = authUser.position === "Co-Instructor"
      || authUser.position === "Instructor Assistant";
    const leadAllowed = isLeadInstructor && !hasSectionAssignments;
    const assistantAllowed = isAssistantOrCo && coversAll;
    if (!leadAllowed && !assistantAllowed) {
      this.setState({ checked: true });
      return;
    }

    // Peer-eval surveys are stage 'initial' or 'final'. Only care about
    // the ones past their end AND whose matching viewed flag on
    // user.viewed_results is still false (i.e. the instructor hasn't
    // acknowledged / generated for this one yet).
    const now = Date.now();
    const isFinished = (s) => s && s.end
      && new Date(s.end).getTime() < now;
    const evalSurveys = allSurveys.filter(s => {
      if (!s || !isFinished(s)) return false;
      const field = VIEWED_FIELD_FOR_STAGE[s.stage];
      if (!field) return false;
      return viewedResults[field] === false;
    });
    if (evalSurveys.length === 0) {
      this.setState({ checked: true });
      return;
    }

    try {
      // For each candidate survey, check (in parallel) whether:
      //   - insights are already generated
      //   - at least one student has completed peer eval
      // Only surface a popup for surveys that (a) have no insights
      // yet, and (b) actually have data to score. Firing the popup on
      // a survey where no student completed peer eval would just send
      // the instructor to a no-op scoring job.
      const withStatus = await Promise.all(
        evalSurveys.map(s => Promise.all([
          this.checkInsightsExist(s.id),
          this.checkAnyCompletion(s.id),
        ]).then(([hasInsights, hasAnyCompletion]) => ({
          survey: s,
          hasInsights,
          hasAnyCompletion,
        }))),
      );
      const missing = withStatus
        .filter(x => !x.hasInsights && x.hasAnyCompletion)
        .map(x => x.survey)
        .sort((a, b) => new Date(b.end) - new Date(a.end));
      const target = missing[0] || null;
      // courseMemberId left null here; it's resolved lazily right
      // before the viewed-flag PATCH on Generate click. The PATCH is
      // fire-and-forget and guarded by `if (courseMemberId)` so the
      // generate flow doesn't block on it.
      this.setState({
        checked: true,
        targetSurvey: target,
        gameId,
      });
    } catch (e) {
      this.setState({ checked: true });
    }
  };

  resolveCourseMemberId = async () => {
    if (this.state.courseMemberId) return this.state.courseMemberId;
    const courseId = Auth.getCourseId();
    const userId = Auth.getUserId();
    if (!courseId || !userId) return null;
    const apiUrl = process.env.REACT_APP_API_URL;
    try {
      const res = await getRequest(
        `${apiUrl}course_members/?course=${courseId}&user=${userId}`
      );
      const body = res && res.body;
      const list = Array.isArray(body && body.results)
        ? body.results
        : (Array.isArray(body) ? body : (body ? [body] : []));
      const selfMember = list.find(m => m && Number(m.user) === Number(userId));
      const id = selfMember ? selfMember.id : null;
      if (id) this.setState({ courseMemberId: id });
      return id;
    } catch (e) {
      return null;
    }
  };

  checkInsightsExist = (surveyId) => {
    const apiUrl = process.env.REACT_APP_API_URL;
    return getRequest(
      `${apiUrl}v2/insights/survey/${surveyId}/students/?page=1&page_size=1`
    )
      .then(res => {
        const body = (res && res.body) || {};
        const results = Array.isArray(body.results) ? body.results : [];
        return results.length > 0;
      })
      .catch(() => false);
  };

  // Peer-eval "at least one student completed" gate. If nobody
  // submitted peer feedback, there's nothing to score and no reason
  // to nudge the instructor toward the scoring/insights job.
  checkAnyCompletion = (surveyId) => {
    const apiUrl = process.env.REACT_APP_API_URL;
    return getRequest(`${apiUrl}v2/games/peer-score/${surveyId}/`)
      .then(res => {
        const body = (res && res.body) || {};
        const rows = Array.isArray(body) ? body : (body.results || []);
        return rows.some(r => r && r.completion === true);
      })
      .catch(() => false);
  };

  // Progress percentage from current stage + polling elapsed.
  progressPct = () => {
    const { stage, pollElapsedMs } = this.state;
    if (stage === 'polling') {
      const min = STAGE_PROGRESS['polling-min'];
      const max = STAGE_PROGRESS['polling-max'];
      const capped = Math.min(POLL_MAX_DURATION_MS, pollElapsedMs);
      const ratio = capped / POLL_MAX_DURATION_MS;
      return Math.round(min + (max - min) * ratio);
    }
    return STAGE_PROGRESS[stage] || 0;
  };

  progressLabel = () => {
    switch (this.state.stage) {
      case 'running-score': return 'Running peer scores…';
      case 'checking-insights': return 'Checking for existing insights…';
      case 'generating': return 'Kicking off insight generation…';
      case 'polling': return 'Generating insights (typically up to 5 minutes)…';
      case 'done': return 'Done!';
      default: return '';
    }
  };

  handleGenerate = async () => {
    const { targetSurvey } = this.state;
    if (!targetSurvey) return;

    // Resolve gameId defensively at click time. state.gameId was
    // captured during evaluate() and can be stale (or zero if the
    // instructor's sessionStorage was missing game_id at mount);
    // Auth.getGameId() reads from sessionStorage now, which is the
    // freshest source we have. Fall back to state.gameId only if
    // Auth is empty.
    const gameId = Number(Auth.getGameId()) || Number(this.state.gameId) || 0;
    if (!gameId) {
      this.setState({
        stage: 'error',
        errorMessage: "Couldn't determine the game context. Please refresh the page and try again.",
      });
      return;
    }

    this.setState({ stage: 'running-score', errorMessage: null });
    const apiUrl = process.env.REACT_APP_API_URL;

    // Fire-and-forget the course_members PATCH so the popup won't
    // resurface on next login regardless of whether the downstream
    // score/generate steps succeed. Instructor's intent has been
    // captured by the button click. courseMemberId is resolved lazily
    // here (one GET to course_members/) rather than at mount — keeps
    // the dashboard first-paint clean of popup-only fetches.
    const field = VIEWED_FIELD_FOR_STAGE[targetSurvey.stage];
    if (field) {
      this.resolveCourseMemberId().then(id => {
        if (!id) return;
        patchRequest(`${apiUrl}course_members/${id}/viewed/`)
          .send({ [field]: true })
          .catch(() => { /* silent — retry naturally next click */ });
      });
    }

    try {
      // Step 1a: calc-score — compute peer scores for this survey
      // from the latest feedback. Fires for both initial and final
      // surveys; peer scores need to exist before Step 1b rolls them
      // into per-user grades.
      await postRequest(
        `${apiUrl}v2/forms/surveys/${targetSurvey.id}/calc-score/`
      );
    } catch (e) {
      this.setState({
        stage: 'error',
        errorMessage: "Couldn't run the peer-score calculation. Please try again.",
      });
      return;
    }

    try {
      // Step 1b: usergrade/run — compile per-user grades (peer scores
      // + team performance + engagement + feedback submission). Runs
      // for both initial and final surveys so the per-user grade is
      // in sync with the peer scores we just computed above.
      //
      // The endpoint parses form-encoded input (matches the working
      // call in final-scores.js). JSON bodies fall through the
      // FormParser and land in the serializer as {} which is why BE
      // reported "game field required" from this component.
      await postRequest(`${apiUrl}v2/games/usergrade/run/`)
        .send({ game: gameId, survey: targetSurvey.id })
        .type("form");
    } catch (e) {
      this.setState({
        stage: 'error',
        errorMessage: "Couldn't run the peer-score job. Please try again.",
      });
      return;
    }

    // Step 2: re-check insights existence in case another
    // instructor already generated between our initial evaluate() and
    // now. Skip generation if so.
    this.setState({ stage: 'checking-insights' });
    let alreadyThere = false;
    try {
      alreadyThere = await this.checkInsightsExist(targetSurvey.id);
    } catch (e) {
      // Ignore — treat as "not there" so we still try to generate.
      alreadyThere = false;
    }
    if (alreadyThere) {
      this.setState({ stage: 'done', dismissed: true });
      return;
    }

    // Step 3: kick off insights job + poll status_url.
    this.setState({ stage: 'generating' });
    try {
      const res = await postRequest(`${apiUrl}v2/insights/generate/${targetSurvey.id}/`).send({});
      const body = (res && res.body) || {};
      const statusUrl = body.status_url;
      if (!statusUrl) {
        // BE didn't return a status_url — assume job is queued and
        // dismiss. Instructor can refresh later to see if it landed.
        this.setState({ stage: 'done', dismissed: true });
        return;
      }
      this._startPolling(statusUrl);
    } catch (e) {
      this.setState({
        stage: 'error',
        errorMessage: "Couldn't kick off insight generation. Please try again.",
      });
    }
  };

  _startPolling = (statusUrl) => {
    this._pollStartTime = Date.now();
    this.setState({ stage: 'polling', pollElapsedMs: 0 });
    // Tick the local elapsed counter every second so the bar visibly
    // fills between actual poll responses.
    this._pollTicker = setInterval(() => {
      const elapsed = Date.now() - this._pollStartTime;
      this.setState({ pollElapsedMs: elapsed });
      if (elapsed > POLL_MAX_DURATION_MS) {
        this._stopPolling();
        this.setState({
          stage: 'error',
          errorMessage: 'Still processing. Please refresh in a moment to check again.',
        });
      }
    }, 1000);
    this._scheduleNextPoll(statusUrl, POLL_FIRST_DELAY_MS);
  };

  _scheduleNextPoll = (statusUrl, delay) => {
    this._pollTimeout = setTimeout(() => this._pollOnce(statusUrl), delay);
  };

  _pollOnce = (statusUrl) => {
    const elapsed = Date.now() - this._pollStartTime;
    if (elapsed > POLL_MAX_DURATION_MS) {
      this._stopPolling();
      return;
    }
    const url = this._resolveStatusUrl(statusUrl);
    getRequest(url)
      .then(res => {
        const body = (res && res.body) || {};
        const status = body.status;
        if (status === 'done') {
          this._stopPolling();
          this.setState({ stage: 'done', dismissed: true });
          return;
        }
        if (status === 'error' || status === 'failed') {
          this._stopPolling();
          this.setState({
            stage: 'error',
            errorMessage: body.error || 'Insight generation failed. Please try again.',
          });
          return;
        }
        // Still running — schedule next poll based on elapsed time.
        const nowElapsed = Date.now() - this._pollStartTime;
        const nextDelay = nowElapsed < POLL_MID_THRESHOLD_MS
          ? POLL_FIRST_DELAY_MS
          : nowElapsed < POLL_LATE_THRESHOLD_MS
            ? POLL_MID_DELAY_MS
            : POLL_LATE_DELAY_MS;
        this._scheduleNextPoll(statusUrl, nextDelay);
      })
      .catch(() => {
        // Transient error — keep polling on the same cadence.
        this._scheduleNextPoll(statusUrl, POLL_MID_DELAY_MS);
      });
  };

  _stopPolling = () => {
    if (this._pollTimeout) {
      clearTimeout(this._pollTimeout);
      this._pollTimeout = null;
    }
    if (this._pollTicker) {
      clearInterval(this._pollTicker);
      this._pollTicker = null;
    }
  };

  _resolveStatusUrl = (statusUrl) => {
    const apiUrl = process.env.REACT_APP_API_URL;
    if (statusUrl.startsWith('/api/')) {
      return `${apiUrl}${statusUrl.slice(5)}`;
    }
    return `${apiUrl}${statusUrl.replace(/^\//, '')}`;
  };

  render() {
    const { checked, targetSurvey, stage, errorMessage, dismissed } = this.state;
    if (!checked || dismissed || !targetSurvey) return null;

    const isBusy = ['running-score', 'checking-insights', 'generating', 'polling'].includes(stage);
    const label = this.progressLabel();
    const pct = this.progressPct();

    return (
      <Backdrop>
        <Card onClick={e => e.stopPropagation()}>
          <Title>
            {targetSurvey.stage === 'initial'
              ? 'Initial Survey Ready to Review'
              : 'Final Survey Ready to Review'}
          </Title>
          <Description>
            Peer feedback has been collected for this survey. Run the
            scoring job and generate insights so students can review
            their results.
          </Description>
          <Actions>
            <PrimaryButton
              type="button"
              onClick={this.handleGenerate}
              disabled={isBusy}
            >
              {isBusy ? 'Working…' : 'Generate Peer Scores & Insights'}
            </PrimaryButton>
          </Actions>
          {isBusy && (
            <ProgressWrap>
              <ProgressLabel>{label}</ProgressLabel>
              <ProgressTrack>
                <ProgressFill pct={pct} />
              </ProgressTrack>
            </ProgressWrap>
          )}
          {errorMessage && <ErrorMsg>{errorMessage}</ErrorMsg>}
        </Card>
      </Backdrop>
    );
  }
}

export default InstructorSurveyReadyNotification;



// WEBPACK FOOTER //
// ./src/components/instructor-notifications/InstructorSurveyReadyNotification.js